
import 'mosha-vue-toastify/dist/style.css';
import { computed, defineComponent, PropType } from 'vue';
import { createToast } from 'mosha-vue-toastify';
import { Bid, Material, Truck, MaterialListItem } from '@/types/app';
import { VendorTruckCost } from '@/types/app/vendor-truck-cost';
import { Route } from '@/types';
import { calculateRouteCostWithTruck, getRouteDuration } from '@/utils';
import { useStore } from '@/store';
import { VolumeAndWeightUnits } from '@/enums';
import { NumberWithMetric, Location } from '@/types/shared';
import MaterialIcon from '@/components/MaterialIcon.vue';
import MaterialIconButton from '@/components/form-controls/MaterialIconButton.vue';
import { formDefaults } from '@/constants';
import { formatNum } from '@/utils/route-utilities';
import { GeoPoint } from '@firebase/firestore-types';

export default defineComponent({
  name: 'VendorListing',
  components: {
    MaterialIcon,
    MaterialIconButton
  },
  props: {
    route: {
      type: Object as PropType<Route | null>
    },
    material: {
      type: Object as PropType<Material>
    },
    markup: {
      type: Number
    },
    taxExempt: {
      type: Boolean
    },
    customTruckData: {
      type: Object as PropType<Record<string, Pick<Truck, 'runRate' | 'unloadLoadTime' | 'enabled' | 'capacity' | 'weight'>>>
    },
    // TODO: Add in search and calculation changes based on quantity of material wanted.
    quantity: {
      type: Object as PropType<NumberWithMetric<VolumeAndWeightUnits>>,
      required: true
    },
    sortBy: {
      type: String,
      required: true
    },
    minTime: {
      type: Number,
      required: true
    },
    location: {
      type: Object as PropType<Location | undefined>
    }
  },
  setup(props) {
    const store = useStore();
    const trucks = computed(() => store.getters.trucks.get);

    const oneWayTripDuration = computed(
      () => (props.route?.directionsResult && getRouteDuration(props.route?.directionsResult) / 60) ?? 0
    );

    const selectedMaterialStock = computed(() => {
      return props.route?.vendor.materials.find(stock => stock.material.name === props.material?.name);
    });

    const computedLocalBid = computed(() => store.getters.bids.getLocalBid ?? formDefaults.localBid);

    const defaultLocation: Location = {
      geoPoint: {
        latitude: 0,
        longitude: 0,
        isEqual: (other: GeoPoint) => other.latitude === 0 && other.longitude === 0
      },
      address: ''
    };

    const alreadyInBid = (truckName: string, materialName: string, vendorName: string, location: Location | undefined) => {
      if (computedLocalBid.value.materialList.length === 0 && location === undefined) {
        return false;
      }

      return computedLocalBid.value.materialList.some((bidItem: MaterialListItem) => {
        const itemLocation = bidItem.location ?? defaultLocation;
        const sameLocation =
          itemLocation.geoPoint.latitude === location?.geoPoint.latitude &&
          itemLocation.geoPoint.longitude === location?.geoPoint.longitude;

        const inBid =
          bidItem.truckDisplayName === truckName &&
          bidItem.materialDisplayName === materialName &&
          bidItem.vendorDisplayName === vendorName &&
          sameLocation;
        return inBid;
      });
    };

    const vendorTruckCosts = computed(() =>
      trucks.value
        .filter(truck => {
          // Check if the truck is enabled based on the custom data or the original data
          const isEnabled = props.customTruckData?.[truck.name]?.enabled ?? truck.enabled;
          return isEnabled;
        })
        .map(truck => {
          const truckWithPossibleCustomData: Truck = {
            ...truck,
            runRate: props.customTruckData?.[truck.name]?.runRate ?? truck.runRate,
            unloadLoadTime: props.customTruckData?.[truck.name]?.unloadLoadTime ?? truck.unloadLoadTime,
            weight: props.customTruckData?.[truck.name]?.weight ?? truck.weight,
            capacity: props.customTruckData?.[truck.name]?.capacity ?? truck.capacity
          };

          const routeCostWithTruck = calculateRouteCostWithTruck({
            truck: truckWithPossibleCustomData,
            selectedMaterialStock: selectedMaterialStock.value,
            route: props.route,
            taxExempt: props.taxExempt,
            // TODO: Use localized tax percentages.
            // For now we will hardcode in Michigan's state tax.
            tax: 0.06,
            doApplyMarkup: props.route?.vendor.doApplyMarkup,
            markup: props.markup,
            quantity: props.quantity.number,
            quantityMetric: props.quantity.metric,
            minTime: props.minTime
          });

          return {
            truck,
            ...routeCostWithTruck
          };
        })
    );

    const sortedVendorTruckCost = computed(() =>
      vendorTruckCosts.value
        .filter(vendorTruckCost => vendorTruckCost.truck.enabled ?? true)
        .slice()
        .sort((a, b) => a.truck.capacity - b.truck.capacity)
    );

    const addBoldClassIfSortBy = (sortByValue: string) => (sortByValue === props.sortBy ? 'bold' : '');

    const getButtonClass = (vendorTruckCost: VendorTruckCost) => {
      if (
        alreadyInBid(vendorTruckCost.truck.name, props.material?.name ?? '', props.route?.vendor.name ?? '', props.location)
      ) {
        return 'disabledButton';
      }
      return '';
    };

    const addToBid = (vendorTruckCost: VendorTruckCost) => {
      if (
        alreadyInBid(vendorTruckCost.truck.name, props.material?.name ?? '', props.route?.vendor.name ?? '', props.location)
      ) {
        return;
      }
      const localBid = store.getters.bids.getLocalBid as Bid;
      store.dispatch.bids.updateLocalBid({
        materialList: [
          ...localBid.materialList,
          {
            material: props.material,
            materialDisplayName: props.material?.name,
            quantity: props.quantity,
            vendor: props.route?.vendor,
            vendorDisplayName: props.route?.vendor.name,
            taxValue: vendorTruckCost.calculationTooltip.totalTax.formatted,
            pricePerUnit: {
              number: vendorTruckCost.finalRate,
              metric: props.quantity.metric
            },
            truck: vendorTruckCost.truck,
            truckDisplayName: vendorTruckCost.truck.name,
            location: props.location ?? defaultLocation
          }
        ]
      } as Bid);

      createToast(
        {
          title: 'Route added to bid!',
          description: `${props.quantity.number} ${props.quantity.metric} of ${props.material?.name} at $${vendorTruckCost.finalRate} / ${props.quantity.metric}`
        },
        {
          position: 'top-center',
          transition: 'slide',
          toastBackgroundColor: 'hsla(90, 4%, 89%)'
        }
      );
    };

    const isInBid = (vendorName: string) => {
      return computedLocalBid.value.materialList
        .filter((materialListItem: MaterialListItem) => materialListItem.vendorDisplayName === vendorName)
        .map((materialListItem: MaterialListItem) => ({
          materialDisplayName: materialListItem.materialDisplayName,
          materialQuantity: materialListItem.quantity.number,
          materialMetric: materialListItem.quantity.metric
        }));
    };

    const getMaterialsTitle = (vendorName: string) => {
      const materials = isInBid(vendorName);
      return materials
        .map(
          (material: { materialQuantity: number; materialMetric: VolumeAndWeightUnits; materialDisplayName: string }) =>
            `${material.materialQuantity} ${material.materialMetric} ${material.materialDisplayName}`
        )
        .join('\n'); // Join with newline character
    };

    return {
      sortedVendorTruckCost,
      selectedMaterialStock,
      oneWayTripDuration,
      addBoldClassIfSortBy,
      addToBid,
      formatNum,
      isInBid,
      getMaterialsTitle,
      alreadyInBid,
      getButtonClass
    };
  }
});
